/// <amd-module name="VSS/Features/Markdown/Plugins/TableOfSubPagesPlugin" />
import * as MarkdownIt from "markdown-it";
import { IState } from "VSS/Features/Markdown/MarkdownIt";

export interface ISubPage {
    name: string;
    remoteUrl: string;
    subPages: ISubPage[];
}

export interface ITableOfSubPagesPluginOptions {
    subPages?: ISubPage[];
    markerPattern?: RegExp;
    containerClass?: string;
    containerHeaderHtml?: string;
    containerFooterHtml?: string;
    listType?: string;
}

export class TableOfSubPagesPlugin {
    private _options: ITableOfSubPagesPluginOptions;
    private _defaultOptions = {
        markerPattern: /^\[\[_TOSP_\]\]/im,
        containerClass: "tosp-container",
        containerHeaderHtml: "",
        containerFooterHtml: "",
        listType: "ul"
    }
    private _codeOfLeftSquareBracket = 0x5b;

    constructor(markdown: MarkdownIt.MarkdownIt, options: ITableOfSubPagesPluginOptions) {
        this._options = { ...this._defaultOptions, ...options };
        // Adding rules to the markdown renderer for tags with names of properties (ex. tosp_open).
        markdown.renderer.rules.tosp_open = this._tospOpenHandler;
        markdown.renderer.rules.tosp_body = this._tospBodyHandler;
        markdown.renderer.rules.tosp_close = this._tospCloseHandler;
        // Insert TOSP rule
        markdown.inline.ruler.after("emphasis", "tosp", this.titlesTransformer)
    }

    private titlesTransformer = (state: IState, silent: boolean): boolean => {
        // Reject if the token does not start with [
        if (state.src.charCodeAt(state.pos) !== this._codeOfLeftSquareBracket) return false;
        // Don't run any pairs in validation mode
        if (silent) return false;

        let match;
        if (this._options.markerPattern) {
            match = this._options.markerPattern.exec(state.src);
        }
        match = !match ? [] : match.filter(this._isDefined);
        // Return if none were found.
        if (match.length < 1) return false;
        // Check the index of match is same as pos
        // This we do because we push tokens as state.pos only, so the match has to be in that index
        if (state.pos !== state.src.indexOf(match[0])) return false;
        // Build content. `state.push` adds new token to "stream". Arguments: type, tag, nesting.
        // For more see Token in lib documentation.
        state.push("tosp_open", "tosp", 1);
        state.push("tosp_body", "", 0);
        state.push("tosp_close", "tosp", -1);
        // Update pos so the parser can continue. Basically move pos to new line.
        const newline = state.src.indexOf("\n", state.pos);
        if (newline !== -1) {
            state.pos = newline;
        } else {
            state.pos = state.pos + state.posMax + 1;
        }

        return true;
    }

    private _isDefined = (value: string) => !!value;

    private _tospOpenHandler = (): string => {
        let tospOpenHtml = ""
        if (this._options) tospOpenHtml = '<div class="' + this._options.containerClass + '">';
        if (this._options && this._options.containerHeaderHtml) tospOpenHtml += this._options.containerHeaderHtml;
        return tospOpenHtml;
    };

    private _tospCloseHandler = (): string => {
        let tospFooterHtml = "";
        if (this._options && this._options.containerFooterHtml) tospFooterHtml = this._options.containerFooterHtml;
        return tospFooterHtml + "</div>";
    };

    private _tospBodyHandler = () => this._renderChildsTokens(this._options.subPages || []);

    private _renderChildsTokens = (pages: ISubPage[]): string => {
        let listOfTitles = [],
            size = pages.length || 0,
            index = 0;

        while (index < size) {
            const currentPage = pages[index];
            listOfTitles.push(`<li><a href="${currentPage.remoteUrl}">${currentPage.name}</a>`);
            if (currentPage.subPages.length) listOfTitles.push(this._renderChildsTokens(currentPage.subPages))
            listOfTitles.push("</li>");
            index++;
        }

        return `<${this._options.listType}>${listOfTitles.join("")}</${this._options.listType}>`;
    };
}
